// Imports ------------------------------------------------------------------->

import { attr, Component } from '../Component'
import { element } from '../CustomElement'
import { i18n } from '../../i18n/I18n'

import { Page, renderFooter, renderSDEFooter } from '../flipp-page/Page'
import {
    FlyerListingItem,
    FlyerListingItemContext,
} from '../flipp-flyer-listing-item/FlyerListingItem'
import { LoadingSpinner } from '../flipp-loading/LoadingSpinner'
import { AppRating } from '../flipp-app-rating/AppRating'

import { inject } from '../../services/di'
import { Flyer } from '../../definitions/FlyerDefinitions'
import { Country, LocationService } from '../../services/LocationService'
import { FlyerService } from '../../services/FlyerService'
import { AnalyticsService } from '../../services/AnalyticsService'
import { StorageService } from '../../services/StorageService'
import { ConfigService, Experience } from '../../services/ConfigService'
import { FlyerPlacement } from '../../services/definitions/IAnalyticsService'
import { UserAgentService } from '../../services/UserAgentService'
import FlyersSortService from '../../services/FlyersSortService'
import FeedbackService from '../../services/FeedbackService'
import { BlackFridayService } from '../../services/BlackFridayService'
import { BannerService } from '../../services/BannerService'
import {
    AdAdaptedService,
    FLYER_LIST_AD_SLOT_NAME_BASE,
    FLYER_LIST_AD_SLOTS_CONFIG,
    FLYER_LIST_AD_SLOTS_WRAPPER_NAME,
    FlyerListAdSlotConfig,
} from '../../services/AdAdaptedService'
import { insert } from '../../utils/array'
import { keys, values } from 'lodash-es'
import { ProudlyCanadianBanner } from '../flipp-proudly-canadian-banner/ProudlyCanadianBanner'
import { CategoryService } from '../../services/CategoryService'

interface ListingPageAttributes {
    category?: string
    withSidebar?: boolean
    sort?: string
}

@element('flipp-listing-page')
export class ListingPage extends Component<ListingPageAttributes, Flyer[]> {
    @attr category: string = 'All Flyers'
    @attr sort: string = 'featured'
    @attr withSidebar: boolean = true

    refs: {
        title: HTMLElement
        content: HTMLElement
        page: HTMLElement
    }

    state = {
        scrollTop: 0,
    }

    render() {
        return (
            <Page
                ref="page"
                show="flyers"
                onScroll={this._handleScroll}
                withSDETopbar={true}
            >
                <ListingPageContent sort={this.sort} category={this.category} />
            </Page>
        )
    }

    onRender() {
        this.refs.page.scrollTop = this.state.scrollTop
    }

    onAttach() {
        this.refs.page.scrollTop = this.state.scrollTop
    }

    private _handleScroll = () => {
        this.state.scrollTop = this.refs.page.scrollTop
    }
}

// --- flipp-listing-page-content --->

interface ListingPageContentAttributes {
    category?: string
    sort?: string
    shouldDivideFlyers?: boolean
}

@element('flipp-listing-page-content')
export class ListingPageContent extends Component<
    ListingPageContentAttributes,
    Flyer[]
> {
    @inject location: LocationService
    @inject flyerService: FlyerService
    @inject analytics: AnalyticsService
    @inject storage: StorageService
    @inject config: ConfigService
    @inject categoryService: CategoryService
    @inject userAgent: UserAgentService
    @inject flyersSortService: FlyersSortService
    @inject feedbackService: FeedbackService
    @inject blackFridayService: BlackFridayService
    @inject bannerService: BannerService
    @inject adAdapted: AdAdaptedService

    @attr category: string = 'All Flyers'
    @attr sort: string = 'featured'
    @attr shouldDivideFlyers: boolean = false

    isAdInited = false

    prepare() {
        return this.flyerService.getFlyers()
    }

    onAttributeChange(name: string, oldVal: string, newVal: string) {
        if (name === 'sort' && this.adAdapted.shouldDisplayAd()) {
            this.isAdInited = false
        }
        super.onAttributeChange(name, oldVal, newVal)
    }

    onAttach() {
        window.addEventListener('flipp-ad-loaded', this.onAddLoaded.bind(this))
        window.addEventListener(
            'mobile-banner-closed',
            this.onMobileBannerClosed.bind(this)
        )
    }

    onRender() {
        if (!this.isAdInited && this.config.EXPERIENCE !== Experience.SDE) {
            this.isAdInited = true
            this.adAdapted.init().catch((e) => {
                this.adAdapted.disableAd()
                this.invalidate()
                console.log(e)
            })
        }
    }

    onAddLoaded(e: any) {
        const adZoneAdAvailabilityMap = e.detail.adZoneAdAvailabilityMap
        const loaded = values(adZoneAdAvailabilityMap).some((v) => v === true)

        if (!loaded) {
            // there is no ads in any slot
            this.adAdapted.disableAd()
            this.invalidate()
            console.log('no adds available')
        } else {
            // there are some ads are available, need to hide unavailable
            keys(FLYER_LIST_AD_SLOTS_CONFIG).map((position: string) => {
                const el = document.getElementById(
                    `${FLYER_LIST_AD_SLOTS_WRAPPER_NAME}-${position}`
                )
                const slotConfig = FLYER_LIST_AD_SLOTS_CONFIG[position]
                keys(slotConfig).forEach((device) => {
                    const zoneId = slotConfig[device]
                    if (!adZoneAdAvailabilityMap[zoneId]) {
                        el.classList.add(`${device}-hide`)
                    }
                })
            })
        }
    }

    onMobileBannerClosed() {
        this.isAdInited = false
        this.invalidate()
    }

    hideAdSlotSpinner() {
        const spinnerEl = document.getElementsByClassName(
            'spinner-wrapper show'
        )
        // Hide spinner
        if (spinnerEl.length) {
            spinnerEl[0].className = 'spinner-wrapper'
        }
    }

    renderLoading() {
        return <LoadingSpinner />
    }

    renderPremiumFlyer(flyer: Flyer, index: number) {
        return (
            <span class="premium-flyer-wrapper">
                <FlyerListingItem
                    flyerId={flyer.id}
                    context={FlyerListingItemContext.Listing}
                    premium
                    sde={this.config.EXPERIENCE === Experience.SDE}
                    category={this.category}
                    onClick={this._handleFlyerClick.bind(
                        this,
                        index,
                        FlyerPlacement.Promoted
                    )}
                />
                {index === 2 || (index > 4 && (index + 1) % 3 === 0)
                    ? this.renderFeedbackCard()
                    : null}
            </span>
        )
    }

    renderAd(slots: FlyerListAdSlotConfig, index: number) {
        return (
            <span class="premium-flyer-wrapper">
                <div
                    id={`${FLYER_LIST_AD_SLOTS_WRAPPER_NAME}-${index}`}
                    class={FLYER_LIST_AD_SLOTS_WRAPPER_NAME}
                >
                    {keys(slots).map((device) => {
                        return (
                            <div
                                id={`${FLYER_LIST_AD_SLOT_NAME_BASE}-${slots[device]}`}
                                class={`ad-slot-content-${device}`}
                            />
                        )
                    })}
                </div>
            </span>
        )
    }

    populateAdSlots(premiumFlyersContent: Flyer[]) {
        keys(FLYER_LIST_AD_SLOTS_CONFIG).map((position: string) => {
            if (premiumFlyersContent.length >= Number(position)) {
                premiumFlyersContent = insert(
                    premiumFlyersContent,
                    Number(position),
                    FLYER_LIST_AD_SLOTS_CONFIG[position]
                )
            }
        })
        return premiumFlyersContent
    }

    render(flyers: Flyer[]) {
        let { premiumFlyers, normalFlyers } = this.flyersSortService.filter(
            flyers,
            this.category,
            this.sort
        )
        if (this.config.EXPERIENCE === Experience.SDE) {
            let selectedFlyers: Flyer[] = []
            const normalGroceryFlyers = normalFlyers.filter((f) =>
                f.categories.includes('Groceries')
            )
            const premiumGroceryFlyersCount = premiumFlyers.filter((f) =>
                f.categories.includes('Groceries')
            ).length
            // If there are less than 3 premium grocery flyers, add organic grocery flyers
            if (premiumGroceryFlyersCount < 3) {
                selectedFlyers = normalGroceryFlyers.slice(
                    0,
                    3 - premiumGroceryFlyersCount
                )
            }
            const flyerCount = selectedFlyers.length + premiumFlyers.length
            // If there are less than 4 flyers, add non-grocery flyers
            if (flyerCount < 4) {
                selectedFlyers = normalFlyers
                    .filter((f) => !f.categories.includes('Groceries'))
                    .slice(0, 4 - flyerCount)
            }
            normalFlyers = selectedFlyers
        }

        if (this.isBlogSortOnHost()) {
            premiumFlyers = []
            normalFlyers = []
        }

        let premiumFlyersContent = premiumFlyers
        if (
            this.adAdapted.shouldDisplayAd() &&
            this.sort === 'featured' &&
            premiumFlyers.length > 0
        ) {
            premiumFlyersContent = this.populateAdSlots(premiumFlyersContent)
        }

        // const urlParams = new URLSearchParams(window.location.search)
        // const giveaway = urlParams.get('giveaway') // 'black_friday', 'christmas', 'back_to_school'
        // const giveawayPeriod = giveaway
        //     ? giveaway.toUpperCase()
        //     : this.blackFridayService.getGiveawayPeriod()

        return (
            <div>
                {this.config.EXPERIENCE !== Experience.SDE && (
                    <div class="topbar">
                        <div
                            class="sort-tabs"
                            hidden={
                                premiumFlyers.length === 0 &&
                                normalFlyers.length === 0
                            }
                            onClick={this._handleSort}
                        >
                            <button
                                title={i18n('featured_sort_help')}
                                aria-label={i18n('featured_sort_help')}
                                sort="featured"
                            >
                                {i18n('featured')}
                            </button>
                            <button
                                title={i18n('latest_sort_help')}
                                aria-label={i18n('latest_sort_help')}
                                sort="latest"
                            >
                                {i18n('latest')}
                            </button>
                            <button
                                title={i18n('alphabetical_sort_help')}
                                aria-label={i18n('alphabetical_sort_help')}
                                sort="alphabetical"
                            >
                                {i18n('alphabetical')}
                            </button>
                        </div>

                        <h1 class="title">{this.getH1()}</h1>
                    </div>
                )}
                <div class="content">
                    {this.shouldShowProudlyCanadianBanner() ? (
                        <ProudlyCanadianBanner />
                    ) : null}

                    {premiumFlyersContent.map((item, index) => {
                        if (item instanceof Flyer) {
                            return this.renderPremiumFlyer(item, index)
                        } else if (
                            this.adAdapted.shouldDisplayAd() &&
                            this.config.EXPERIENCE !== Experience.SDE
                        ) {
                            return this.renderAd(item, index)
                        }
                    })}
                    {this.insertDividerAfterPremiumFlyers()}
                    {normalFlyers.map((flyer, idx) => (
                        <FlyerListingItem
                            flyerId={flyer.id}
                            context={FlyerListingItemContext.Listing}
                            category={this.category}
                            onClick={this._handleFlyerClick.bind(
                                this,
                                idx,
                                FlyerPlacement.Organic
                            )}
                        />
                    ))}

                    {premiumFlyers.length === 0 && normalFlyers.length === 0 ? (
                        <div class="empty-item-container">
                            <div>{this.renderNoFlyersMessage()}</div>
                        </div>
                    ) : null}
                    {this.shouldShowProudlyCanadianBanner() &&
                    this.categoryService.isProudlyCanadianCategory() ? (
                        <div class="disclaimer-text">
                            {i18n('proudly_canadian_disclaimer')}
                        </div>
                    ) : null}
                </div>
                {this.config.EXPERIENCE === Experience.SDE
                    ? renderSDEFooter()
                    : !this.blackFridayService.isOnHost()
                      ? renderFooter(this.location.locale)
                      : null}
            </div>
        )
    }

    renderNoFlyersMessage() {
        const isBlackFriday = document.location.href.includes('black-friday')
        if (isBlackFriday) {
            if (this.isBlogSortOnHost()) return null
            return i18n('no_flyers_for_blackfriday_page')
        } else {
            return i18n('no_flyers_for_postal_code', {
                postalCode: this.location.postalCode,
            })
        }
    }

    insertDividerAfterPremiumFlyers() {
        return this.shouldDivideFlyers ? <div /> : null
    }

    private renderFeedbackCard() {
        if (
            this.bannerService.isSmartNews() ||
            this.config.shouldDisableMobileBanners() ||
            !this.userAgent.mobile
        ) {
            return
        }
        const buttonClass = this.location.locale === 'fr-ca' ? 'french' : ''
        return (
            <div class="feedback-card">
                <div class="feedback-image">
                    <img
                        src="https://f.wishabi.net/creative/Distribution/images/icons/feedback-img.png"
                        alt="feedback-image"
                    />
                </div>
                <div class="feedback-text-wrapper">
                    <div class="feedback-text">
                        {this.feedbackService.getFeedback()}
                    </div>
                    <div class="feedback-rating">
                        <AppRating />
                    </div>
                </div>
                <div class="feedback-card-button">
                    <a href="https://click.flipp.com/h8pE/1c6a8a4d">
                        <button class={buttonClass}>{i18n('get_app')}</button>
                    </a>
                </div>
            </div>
        )
    }

    private getH1 = () => {
        let title = 'flyers_for_postal_code_category'
        if (
            this.location.city &&
            this.location.city !== 'undefined' &&
            this.location.city !== 'null'
        ) {
            title = 'flyers_for_city_postal_code_category'
            if (this.category !== 'All Flyers') {
                title = 'category_flyers_for_city_postal_code_category'
            } else {
                title = 'index_flyers_h1'
            }
        }

        return i18n(title, {
            postalCode: this.location.postalCode,
            city: this.location.city,
            category: i18n(this.category),
        })
    }

    shouldShowProudlyCanadianBanner() {
        return (
            this.config.EXPERIENCE !== Experience.SDE &&
            this.location.country === Country.CANADA
        )
    }

    private isBlogSortOnHost = () =>
        this.blackFridayService.isOnHost() && this.sort === 'blog'

    private _handleFlyerClick = (idx: number, placement: FlyerPlacement) => {
        this.analytics.trackFlyerClick(this.category, this.sort, placement, idx)
    }

    private _handleSort = (e: Event) => {
        const target = e.target
        if (
            target instanceof HTMLButtonElement &&
            target.hasAttribute('sort')
        ) {
            this.sort = target.getAttribute('sort')

            this.flyerService.getFlyers().then((flyers) => {
                this.analytics.trackFlyerListing(
                    this.category,
                    flyers,
                    this.sort
                )
            })
        }
    }
}
